import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Lock as LockIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import type { MappingResponse } from '../services/types';

interface SchemaDiffBannerProps {
  mappingResponse: MappingResponse | undefined;
  onReviewBroken?: () => void;
  onReviewStale?: () => void;
}

/**
 * Collapsible banner shown when a recipe was generated in delta mode.
 * Summarises inherited, new, broken, and stale mapping counts.
 */
const SchemaDiffBanner: React.FC<SchemaDiffBannerProps> = ({
  mappingResponse,
  onReviewBroken,
  onReviewStale,
}) => {
  const [expanded, setExpanded] = useState(false);

  const stats = useMemo(() => {
    if (!mappingResponse?.mappings) return null;

    let inherited = 0;
    let broken = 0;
    let stale = 0;
    let aiGenerated = 0;
    let approved = 0;

    for (const m of mappingResponse.mappings) {
      switch (m.field_status) {
        case 'inherited':
          inherited++;
          break;
        case 'broken':
          broken++;
          break;
        case 'stale':
          stale++;
          break;
        case 'approved':
          approved++;
          break;
        default:
          aiGenerated++;
          break;
      }
    }

    // Also count broken/stale mappings from metadata (they may not be in mappings array)
    const metaBroken = mappingResponse.metadata?.broken_mappings;
    const metaStale = mappingResponse.metadata?.stale_mappings;
    if (Array.isArray(metaBroken) && metaBroken.length > broken) {
      broken = metaBroken.length;
    }
    if (Array.isArray(metaStale) && metaStale.length > stale) {
      stale = metaStale.length;
    }

    return { inherited, broken, stale, aiGenerated, approved };
  }, [mappingResponse]);

  const fileRenames = mappingResponse?.metadata?.file_renames;
  const renameCount = fileRenames ? Object.keys(fileRenames).length : 0;

  // Only show in delta mode
  if (mappingResponse?.generation_mode !== 'delta' || !stats) return null;

  return (
    <Alert
      severity={stats.broken > 0 ? 'warning' : 'info'}
      icon={false}
      sx={{
        py: 0.5,
        px: 2,
        borderRadius: 0,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '& .MuiAlert-message': { width: '100%', py: 0 },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ width: '100%' }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Typography variant="body2" fontWeight={600} sx={{ mr: 0.5 }}>
            Delta Generation
          </Typography>
          <Chip
            icon={<LockIcon sx={{ fontSize: 14 }} />}
            label={`${stats.inherited} inherited`}
            size="small"
            sx={{ bgcolor: '#eff6ff', color: '#3b82f6', fontWeight: 600, fontSize: 11 }}
          />
          <Chip
            icon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
            label={`${stats.aiGenerated} new`}
            size="small"
            sx={{ bgcolor: '#f0fdf4', color: '#16a34a', fontWeight: 600, fontSize: 11 }}
          />
          {stats.broken > 0 && (
            <Chip
              icon={<WarningIcon sx={{ fontSize: 14 }} />}
              label={`${stats.broken} broken`}
              size="small"
              onClick={onReviewBroken}
              sx={{
                bgcolor: '#fef2f2',
                color: '#ef4444',
                fontWeight: 600,
                fontSize: 11,
                cursor: onReviewBroken ? 'pointer' : 'default',
                '&:hover': onReviewBroken ? { bgcolor: '#fee2e2' } : {},
              }}
            />
          )}
          {stats.stale > 0 && (
            <Chip
              icon={<RefreshIcon sx={{ fontSize: 14 }} />}
              label={`${stats.stale} stale`}
              size="small"
              onClick={onReviewStale}
              sx={{
                bgcolor: '#fffbeb',
                color: '#f59e0b',
                fontWeight: 600,
                fontSize: 11,
                cursor: onReviewStale ? 'pointer' : 'default',
                '&:hover': onReviewStale ? { bgcolor: '#fef3c7' } : {},
              }}
            />
          )}
        </Stack>
        <IconButton size="small" onClick={() => setExpanded((p) => !p)}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Stack>

      <Collapse in={expanded}>
        <Box sx={{ mt: 1, mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.8 }}>
            This recipe was generated using <strong>delta mode</strong> &mdash; approved
            mappings from the parent recipe were inherited and only new or changed fields
            were re-mapped by AI.
          </Typography>
          {renameCount > 0 && (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
              {renameCount} source file{renameCount > 1 ? 's were' : ' was'} renamed
              and mapping references updated automatically.
            </Typography>
          )}
          {stats.broken > 0 && (
            <Typography variant="caption" color="error.main" component="div" sx={{ mt: 0.5 }}>
              {stats.broken} mapping{stats.broken > 1 ? 's are' : ' is'} broken due to
              schema changes (source/target columns removed or types incompatible).
            </Typography>
          )}
          {stats.stale > 0 && (
            <Typography variant="caption" component="div" sx={{ mt: 0.5, color: '#d97706' }}>
              {stats.stale} mapping{stats.stale > 1 ? 's' : ''} may need review due to
              minor schema changes (compatible type changes or column renames detected).
            </Typography>
          )}
        </Box>
      </Collapse>
    </Alert>
  );
};

export default SchemaDiffBanner;
